import { useCallback, useMemo, useSyncExternalStore } from "react";

import { viewVisual } from "#app/components/view-options/view-type-meta";
import type { EntityViewRow } from "#app/lib/data";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { AnalyticsContext, DashboardEvent, trackEvent } from "#app/lib/analytics";
import type { EntityViewsScope } from "#app/lib/hooks/use-entity-views";
import {
  dashboardRefKey,
  dashboardSnapshot,
  resolveStoredDashboard,
  useDashboardSurfaces,
  type DashboardSurface,
  type ResolvedDashboardRef,
  type StoredDashboardRef,
} from "./use-dashboard-surfaces";

/**
 * The viewer's starred dashboards, mirrored per workspace in localStorage —
 * client-local because the server's favorites table is issue-typed only and
 * dashboards are not a server collection yet. When they become one, this
 * storage is the seed the migration reads.
 *
 * A module store over `useSyncExternalStore`: the writer (the All Dashboards
 * star) and the reader (the module menu's Favorites section) are on screen
 * TOGETHER, so separate hook instances must see one list reactively.
 * `usePersistedState` now shares one state per key the same way (the recents
 * stores ride it); this module predates that and keeps its own entry.
 *
 * Star order is insertion order (newest last), matching how the shelf was
 * built up — favorites are a curated shelf, not a ranking.
 */

const stores = new Map<string, StoredDashboardRef[]>();
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function storageKey(workspaceId: string) {
  return `dashboard-favorites:${workspaceId}`;
}

/** Cached per workspace so `getSnapshot` returns a stable reference. */
function loadFavorites(workspaceId: string): StoredDashboardRef[] {
  const cached = stores.get(workspaceId);
  if (cached) return cached;
  let favorites: StoredDashboardRef[] = [];
  try {
    const raw = globalThis.localStorage?.getItem(storageKey(workspaceId));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (
      parsed &&
      typeof parsed === "object" &&
      Array.isArray((parsed as { favorites?: unknown }).favorites)
    ) {
      favorites = (
        (parsed as { favorites: unknown[] }).favorites
      ).filter(
        (entry): entry is StoredDashboardRef =>
          !!entry &&
          typeof entry === "object" &&
          ((entry as StoredDashboardRef).kind === "workflow" ||
            (entry as StoredDashboardRef).kind === "workspace") &&
          typeof (entry as StoredDashboardRef).id === "string" &&
          typeof (entry as StoredDashboardRef).name === "string",
      );
    }
  } catch {
    // Unreadable storage reads as an empty shelf; the next write repairs it.
  }
  stores.set(workspaceId, favorites);
  return favorites;
}

function writeFavorites(workspaceId: string, next: StoredDashboardRef[]) {
  stores.set(workspaceId, next);
  try {
    globalThis.localStorage?.setItem(
      storageKey(workspaceId),
      JSON.stringify({ favorites: next }),
    );
  } catch {
    // ignore quota / unavailable storage — the in-memory store still updated
  }
  emit();
}

/**
 * The star's storage alone — membership and the toggle — with no surface
 * lookup behind it. Every star OUTSIDE the Dashboards module rides this (a
 * view tab's menu, the Customize view panel, the dashboard page's masthead):
 * those hosts hold the view row itself, which is a complete snapshot, so
 * they never need the surfaces query mounted beside them.
 */
export function useDashboardFavoriteStore() {
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.workspaceId ?? "";

  const stored = useSyncExternalStore(
    subscribe,
    () => loadFavorites(workspaceId),
    () => loadFavorites(workspaceId),
  );

  const favoriteKeys = useMemo(
    () => new Set(stored.map((favorite) => dashboardRefKey(favorite))),
    [stored],
  );

  const isFavorite = useCallback(
    (refKey: string) => favoriteKeys.has(refKey),
    [favoriteKeys],
  );

  const removeFavorite = useCallback(
    (refKey: string) => {
      writeFavorites(
        workspaceId,
        loadFavorites(workspaceId).filter(
          (favorite) => dashboardRefKey(favorite) !== refKey,
        ),
      );
    },
    [workspaceId],
  );

  /** Stars the dashboard the snapshot names, or unstars it if already
   *  starred. The snapshot is what paints before the live data hydrates. */
  const toggleFavoriteRef = useCallback(
    (ref: StoredDashboardRef, via: string) => {
      const key = dashboardRefKey(ref);
      const current = loadFavorites(workspaceId);
      const starred = current.some((favorite) => dashboardRefKey(favorite) === key);
      trackEvent({
        context: AnalyticsContext.Dashboard,
        event: starred ? DashboardEvent.Unfavorite : DashboardEvent.Favorite,
        data: { via, scope: ref.kind },
      });
      if (starred) {
        removeFavorite(key);
        return;
      }
      writeFavorites(workspaceId, [...current, ref]);
    },
    [removeFavorite, workspaceId],
  );

  return { stored, isFavorite, toggleFavoriteRef, removeFavorite };
}

export function useDashboardFavorites() {
  const { stored, isFavorite, toggleFavoriteRef, removeFavorite } =
    useDashboardFavoriteStore();
  const { surfaceByRefKey, isLoaded } = useDashboardSurfaces();

  const favorites = useMemo<ResolvedDashboardRef[]>(() => {
    return stored
      .map((favorite) =>
        resolveStoredDashboard(favorite, surfaceByRefKey, isLoaded),
      )
      .filter((resolved) => resolved !== null);
  }, [stored, surfaceByRefKey, isLoaded]);

  const toggleFavorite = useCallback(
    (surface: DashboardSurface, via: string) =>
      toggleFavoriteRef(dashboardSnapshot(surface), via),
    [toggleFavoriteRef],
  );

  return { favorites, isFavorite, toggleFavorite, removeFavorite };
}

/**
 * The star for a dashboard-type VIEW ROW where it is hosted — a workflow's
 * tab strip or the workspace dashboard page — resolved from the row and its
 * scope alone (a row carries the whole identity snapshot a favorite stores).
 * `null` for any other view type: only dashboards have a favorites shelf.
 */
export function useDashboardViewFavorite(
  view: EntityViewRow | null | undefined,
  scope: EntityViewsScope,
  via: string,
): { favorited: boolean; toggle: () => void } | null {
  const { isFavorite, toggleFavoriteRef } = useDashboardFavoriteStore();
  const ref = useMemo<StoredDashboardRef | null>(() => {
    // A team's views are its record tabs, never dashboards.
    if (!view || view.type !== "dashboard" || scope.kind === "team") return null;
    return {
      kind: scope.kind,
      id: scope.id,
      viewKey: view.builtInKey ?? view.id,
      name: view.name,
      ...viewVisual(view),
    };
  }, [scope.id, scope.kind, view]);
  const toggle = useCallback(() => {
    if (ref) toggleFavoriteRef(ref, via);
  }, [ref, toggleFavoriteRef, via]);
  if (!ref) return null;
  return { favorited: isFavorite(dashboardRefKey(ref)), toggle };
}
